import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { AreaChart, Area, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, LineChart, Line, RadialBarChart, RadialBar, ReferenceLine, LabelList, Tooltip } from "recharts";
import { ChartTooltip, PieChartTooltip } from "@/components/ui/chart-tooltip";
import { Clock, Timer, Target, AlertTriangle, Bot, List, Camera, AlertCircle, TrendingUp, TrendingDown, Activity, ArrowUp, ArrowDown, Minus, Wrench, Users, CheckCircle, BarChart3, Filter, X, Minimize2, Maximize2 } from "lucide-react";
import { FilterState, Reports4KPIConfig } from "@/pages/Reports";
import { PeopleHistogramFilter } from "./PeopleHistogramFilter";
interface Reports4KPIGridProps {
  config: Reports4KPIConfig;
  filters: FilterState;
}

// Mock data
const getMockData = () => ({
  pontualidadeRDO: [{
    dia: '1',
    valor: 85
  }, {
    dia: '2',
    valor: 88
  }, {
    dia: '3',
    valor: 90
  }, {
    dia: '4',
    valor: 87
  }, {
    dia: '5',
    valor: 92
  }, {
    dia: '6',
    valor: 89
  }, {
    dia: '7',
    valor: 91
  }],
  cumprimentoPrazos: [{
    name: 'No Prazo',
    value: 76,
    color: '#10b981',
    percentage: '76.0'
  }, {
    name: 'Atrasado',
    value: 24,
    color: '#ef4444',
    percentage: '24.0'
  }],
  tarefasAtrasadas: [{
    name: 'Atrasadas',
    value: 15,
    color: '#ef4444',
    percentage: '15.0'
  }, {
    name: 'No Prazo',
    value: 85,
    color: '#10b981',
    percentage: '85.0'
  }],
  tarefasPorStatus: [{
    name: 'Não iniciado',
    value: 15,
    color: '#ef4444',
    percentage: '7.9'
  }, {
    name: 'Em execução',
    value: 45,
    color: '#f59e0b',
    percentage: '23.7'
  }, {
    name: 'Concluído',
    value: 120,
    color: '#10b981',
    percentage: '63.2'
  }, {
    name: 'Declinado',
    value: 8,
    color: '#6b7280',
    percentage: '4.2'
  }],
  progressoFisico: [{
    mes: 'Jan',
    planejado: 10,
    real: 8
  }, {
    mes: 'Fev',
    planejado: 25,
    real: 22
  }, {
    mes: 'Mar',
    planejado: 40,
    real: 38
  }, {
    mes: 'Abr',
    planejado: 55,
    real: 48
  }, {
    mes: 'Mai',
    planejado: 70,
    real: 62
  }, {
    mes: 'Jun',
    planejado: 85,
    real: 74
  }],
  equipamentos: [{
    name: 'Próprios',
    value: 86,
    color: '#0071CE',
    percentage: '86.0'
  }, {
    name: 'Locados',
    value: 14,
    color: '#FFC107',
    percentage: '14.0'
  }],
  histogramaPessoas: [{
    periodo: '01/01',
    pessoas: 5
  }, {
    periodo: '03/01',
    pessoas: 25
  }, {
    periodo: '05/01',
    pessoas: 45
  }, {
    periodo: '07/01',
    pessoas: 38
  }, {
    periodo: '09/01',
    pessoas: 42
  }, {
    periodo: '11/01',
    pessoas: 40
  }, {
    periodo: '13/01',
    pessoas: 15
  }, {
    periodo: '15/01',
    pessoas: 20
  }, {
    periodo: '17/01',
    pessoas: 35
  }, {
    periodo: '19/01',
    pessoas: 32
  }, {
    periodo: '21/01',
    pessoas: 28
  }, {
    periodo: '23/01',
    pessoas: 8
  }]
});
const kpiItems = [{
  key: 'pontualidadeRDO',
  title: 'Pontualidade dos RDOs',
  value: '92%',
  trend: '+5%',
  trendUp: true,
  icon: Clock,
  color: 'text-green-600',
  bgColor: 'bg-green-50',
  borderColor: 'border-green-200',
  description: 'vs. último período'
}, {
  key: 'cumprimentoPrazos',
  title: 'Taxa de Cumprimento de Tarefas no Prazo',
  value: '76%',
  trend: '+12%',
  trendUp: true,
  icon: CheckCircle,
  color: 'text-emerald-600',
  bgColor: 'bg-emerald-50',
  borderColor: 'border-emerald-200',
  description: 'melhoria significativa'
}, {
  key: 'tarefasAtrasadas',
  title: 'Tarefas em Atraso',
  value: '23',
  trend: '-7',
  trendUp: true,
  icon: AlertTriangle,
  color: 'text-orange-600',
  bgColor: 'bg-orange-50',
  borderColor: 'border-orange-200',
  description: 'redução vs. última semana'
}, {
  key: 'tarefasStatus',
  title: 'Tarefa por Status',
  value: '156',
  trend: '+12',
  trendUp: true,
  icon: BarChart3,
  color: 'text-cyan-600',
  bgColor: 'bg-cyan-50',
  borderColor: 'border-cyan-200',
  description: 'distribuição atual'
}, {
  key: 'progressoFisico',
  title: 'Progresso Físico (Real x planejado)',
  value: '85% / 74%',
  trend: '+3',
  trendUp: true,
  icon: Target,
  color: 'text-indigo-600',
  bgColor: 'bg-indigo-50',
  borderColor: 'border-indigo-200',
  description: 'vs. planejado'
}, {
  key: 'equipamentos',
  title: 'Equipamentos',
  value: '45/52',
  trend: '+3',
  trendUp: true,
  icon: Wrench,
  color: 'text-amber-600',
  bgColor: 'bg-amber-50',
  borderColor: 'border-amber-200',
  description: 'próprio x locado'
}, {
  key: 'histogramaPessoas',
  title: 'Histograma de pessoas',
  value: '387',
  trend: '+23',
  trendUp: true,
  icon: Users,
  color: 'text-violet-600',
  bgColor: 'bg-violet-50',
  borderColor: 'border-violet-200',
  description: 'pessoas ativas'
}];
const TrendIndicator = ({
  value,
  trend
}: {
  value: number;
  trend: 'up' | 'down' | 'stable';
}) => {
  const Icon = trend === 'up' ? ArrowUp : trend === 'down' ? ArrowDown : Minus;
  const color = trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-600' : 'text-gray-600';
  return <div className={`flex items-center gap-1 ${color} absolute bottom-3 right-3`}>
      <Icon className="h-3 w-3" />
      <span className="text-xs font-medium">{Math.abs(value)}%</span>
    </div>;
};
// Os KPIs abaixo usam dados de exemplo. Até serem religados às agregações
// reais dos RDOs/tarefas (obraStats), a grade exibe o aviso e não números
// inventados. A implementação original segue preservada em
// Reports4KPIGridPreview para a religação futura.
export const Reports4KPIGrid = (_props: Reports4KPIGridProps) => {
  return (
    <Card className="p-10">
      <div className="flex flex-col items-center justify-center text-center gap-3 text-muted-foreground">
        <BarChart3 className="h-10 w-10 opacity-40" />
        <div>
          <p className="font-medium text-foreground">Indicadores em construção</p>
          <p className="text-sm max-w-xl">
            Os KPIs desta página serão calculados a partir dos RDOs e tarefas
            reais das suas obras. Enquanto isso, acompanhe os números no
            painel de cada obra (Dashboard → obra).
          </p>
        </div>
      </div>
    </Card>
  );
};

const Reports4KPIGridPreview = ({
  config,
  filters
}: Reports4KPIGridProps) => {
  const data = getMockData();
  const activeKPIs = kpiItems.filter(item => config[item.key as keyof Reports4KPIConfig]);
  const [minimizedCards, setMinimizedCards] = React.useState<Set<string>>(new Set());
  const [hiddenCards, setHiddenCards] = React.useState<Set<string>>(new Set());
  const toggleMinimize = (cardKey: string) => {
    setMinimizedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(cardKey)) {
        newSet.delete(cardKey);
      } else {
        newSet.add(cardKey);
      }
      return newSet;
    });
  };
  const hideCard = (cardKey: string) => {
    setHiddenCards(prev => new Set([...prev, cardKey]));
  };
  return <div className="space-y-8">
      {/* Chart-based KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {/* Progresso Físico - Com coluna mais grossa para planejado */}
        {config.progressoFisico && !hiddenCards.has('progressoFisico') && <Card className={`hover:shadow-lg transition-all duration-300 cursor-pointer bg-white border ${minimizedCards.has('progressoFisico') ? 'p-4 col-span-1' : 'p-6 col-span-1 md:col-span-2 lg:col-span-3 xl:col-span-4'}`}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-gray-50 shadow-sm">
                  <Target className="h-6 w-6 text-primary" />
                </div>
                {minimizedCards.has('progressoFisico') && <h3 className="font-medium text-sm text-muted-foreground">
                    Progresso Físico
                  </h3>}
              </div>
              <div className="flex items-center gap-2">
                {!minimizedCards.has('progressoFisico') && <Badge variant="default" className="gap-1">
                    <TrendingUp className="h-3 w-3" />
                    +3
                  </Badge>}
                <button onClick={() => toggleMinimize('progressoFisico')} className="inline-flex h-11 w-11 items-center justify-center rounded transition-colors hover:bg-muted sm:h-7 sm:w-7">
                  {minimizedCards.has('progressoFisico') ? <Maximize2 className="h-4 w-4 text-gray-500" /> : <Minimize2 className="h-4 w-4 text-gray-500" />}
                </button>
                <button onClick={() => hideCard('progressoFisico')} className="inline-flex h-11 w-11 items-center justify-center rounded transition-colors hover:bg-muted sm:h-7 sm:w-7">
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              </div>
            </div>
            {minimizedCards.has('progressoFisico') && (
              <div className="py-2">
                <div className="text-2xl font-bold text-foreground text-center">85% / 74%</div>
                <p className="text-xs text-muted-foreground text-center">Planejado x Realizado</p>
              </div>
            )}
        {!minimizedCards.has('progressoFisico') && <>
                <div className="space-y-2 mb-4">
                  <h3 className="font-medium text-sm text-muted-foreground">
                    Progresso Físico
                  </h3>
                  <div className="text-3xl font-bold text-foreground">
                    85% / 74%
                  </div>
                   <p className="text-xs text-muted-foreground">
                     planejado / realizado
                   </p>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Planejado x Realizado</span>
                    <span className="font-medium text-foreground">85% / 74%</span>
                  </div>
                  <div className="relative h-6 bg-muted rounded-full overflow-hidden">
                    {/* Barra do Planejado (menos opaca e mais grossa) */}
                    <div className="absolute top-0 left-0 h-full bg-primary/40 rounded-full transition-all duration-300" style={{
                width: '85%'
              }} />
                    {/* Barra do Realizado (mais opaca e menos grossa) */}
                    <div className="absolute top-1.5 left-0 h-3 bg-primary rounded-full transition-all duration-300" style={{
                width: '74%'
              }} />
                  </div>
                </div>
              </>}
          </Card>}

        {/* Histograma de pessoas */}
        {config.histogramaPessoas && !hiddenCards.has('histogramaPessoas') && <Card className={`hover:shadow-lg transition-all duration-300 cursor-pointer bg-white border ${minimizedCards.has('histogramaPessoas') ? 'p-4 col-span-1' : 'p-6 col-span-1 md:col-span-2'}`}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-gray-50 shadow-sm">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                {minimizedCards.has('histogramaPessoas') && <h3 className="font-medium text-sm text-muted-foreground">
                    Histograma de pessoas
                  </h3>}
                {!minimizedCards.has('histogramaPessoas') && <PeopleHistogramFilter onFilterChange={filters => console.log('Filtros selecionados:', filters)} />}
              </div>
              <div className="flex items-center gap-2">
                {!minimizedCards.has('histogramaPessoas') && <Badge variant="default" className="gap-1">
                    <TrendingUp className="h-3 w-3" />
                    +23
                  </Badge>}
                <button onClick={() => toggleMinimize('histogramaPessoas')} className="inline-flex h-11 w-11 items-center justify-center rounded transition-colors hover:bg-muted sm:h-7 sm:w-7">
                  {minimizedCards.has('histogramaPessoas') ? <Maximize2 className="h-4 w-4 text-gray-500" /> : <Minimize2 className="h-4 w-4 text-gray-500" />}
                </button>
                <button onClick={() => hideCard('histogramaPessoas')} className="inline-flex h-11 w-11 items-center justify-center rounded transition-colors hover:bg-muted sm:h-7 sm:w-7">
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              </div>
            </div>
            {minimizedCards.has('histogramaPessoas') && (
              <div className="py-2">
                <div className="text-2xl font-bold text-foreground text-center">41</div>
              </div>
            )}
        {!minimizedCards.has('histogramaPessoas') && <>
                <div className="space-y-2 mb-4">
                  <h3 className="font-medium text-sm text-muted-foreground">
                    Histograma de pessoas
                  </h3>
                  <div className="text-3xl font-bold text-foreground">
                     41
                  </div>
                  <p className="text-xs text-muted-foreground">pessoas em média</p>
                </div>
                <ResponsiveContainer width="100%" height={120}>
                    <LineChart data={data.histogramaPessoas}>
                      <XAxis dataKey="periodo" fontSize={10} />
                      <YAxis fontSize={12} domain={[0, 50]} />
                      <Tooltip content={<ChartTooltip />} />
                      <Line type="monotone" dataKey="pessoas" stroke="hsl(var(--primary))" strokeWidth={3} dot={{
                  fill: 'hsl(var(--primary))',
                  strokeWidth: 2,
                  r: 4
                }} />
                  </LineChart>
                </ResponsiveContainer>
              </>}
          </Card>}

        {/* Pontualidade RDO - Gráfico de linha com meta em 90% */}
        {config.pontualidadeRDO && !hiddenCards.has('pontualidadeRDO') && <Card className={`hover:shadow-lg transition-all duration-300 cursor-pointer bg-white border ${minimizedCards.has('pontualidadeRDO') ? 'p-4 col-span-1' : 'p-6 col-span-1 md:col-span-2'}`}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-gray-50 shadow-sm">
                  <Clock className="h-6 w-6 text-primary" />
                </div>
                {minimizedCards.has('pontualidadeRDO') && <h3 className="font-medium text-sm text-muted-foreground">
                    Pontualidade dos RDOs
                  </h3>}
              </div>
              <div className="flex items-center gap-2">
                {!minimizedCards.has('pontualidadeRDO') && <Badge variant="default" className="gap-1">
                    <TrendingUp className="h-3 w-3" />
                    +5%
                  </Badge>}
                <button onClick={() => toggleMinimize('pontualidadeRDO')} className="inline-flex h-11 w-11 items-center justify-center rounded transition-colors hover:bg-muted sm:h-7 sm:w-7">
                  {minimizedCards.has('pontualidadeRDO') ? <Maximize2 className="h-4 w-4 text-gray-500" /> : <Minimize2 className="h-4 w-4 text-gray-500" />}
                </button>
                <button onClick={() => hideCard('pontualidadeRDO')} className="inline-flex h-11 w-11 items-center justify-center rounded transition-colors hover:bg-muted sm:h-7 sm:w-7">
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              </div>
            </div>
            {minimizedCards.has('pontualidadeRDO') && (
              <div className="py-2">
                <div className="text-2xl font-bold text-foreground text-center">92%</div>
              </div>
            )}
        {!minimizedCards.has('pontualidadeRDO') && <>
                <div className="space-y-2 mb-4">
                  <h3 className="font-medium text-sm text-muted-foreground">
                    Pontualidade dos RDOs
                  </h3>
                  <div className="text-3xl font-bold text-foreground">
                    92%
                  </div>
                  <p className="text-xs text-muted-foreground">
                    vs. último período
                  </p>
                </div>
                <div className="relative">
                  <div className="absolute top-[24px] left-[70px] text-xs text-red-500 font-medium z-10">
                    Meta
                  </div>
                  <ResponsiveContainer width="100%" height={120}>
                    <LineChart data={data.pontualidadeRDO} margin={{
                left: 50,
                right: 10
              }}>
                      <XAxis dataKey="dia" fontSize={12} />
                      <YAxis domain={[80, 100]} fontSize={12} />
                      <Tooltip content={<ChartTooltip />} />
                      <ReferenceLine y={90} stroke="#ef4444" strokeDasharray="5 5" strokeWidth={2} />
                      <Line type="monotone" dataKey="valor" stroke="hsl(var(--primary))" strokeWidth={3} dot={{
                  fill: 'hsl(var(--primary))',
                  strokeWidth: 2,
                  r: 4
                }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </>}
          </Card>}

        {/* Cumprimento de Prazos - Gráfico de pizza */}
        {config.cumprimentoPrazos && !hiddenCards.has('cumprimentoPrazos') && <Card className={`hover:shadow-lg transition-all duration-300 cursor-pointer bg-white border ${minimizedCards.has('cumprimentoPrazos') ? 'p-4' : 'p-6'}`}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-gray-50 shadow-sm">
                  <CheckCircle className="h-6 w-6 text-primary" />
                </div>
                {minimizedCards.has('cumprimentoPrazos') && <h3 className="font-medium text-sm text-muted-foreground">
                    Taxa de Cumprimento de Tarefas no Prazo
                  </h3>}
              </div>
              <div className="flex items-center gap-2">
                {!minimizedCards.has('cumprimentoPrazos') && <Badge variant="default" className="gap-1">
                    <TrendingUp className="h-3 w-3" />
                    +12%
                  </Badge>}
                <button onClick={() => toggleMinimize('cumprimentoPrazos')} className="inline-flex h-11 w-11 items-center justify-center rounded transition-colors hover:bg-muted sm:h-7 sm:w-7">
                  {minimizedCards.has('cumprimentoPrazos') ? <Maximize2 className="h-4 w-4 text-gray-500" /> : <Minimize2 className="h-4 w-4 text-gray-500" />}
                </button>
                <button onClick={() => hideCard('cumprimentoPrazos')} className="inline-flex h-11 w-11 items-center justify-center rounded transition-colors hover:bg-muted sm:h-7 sm:w-7">
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              </div>
            </div>
            {minimizedCards.has('cumprimentoPrazos') && (
              <div className="py-2">
                <div className="text-2xl font-bold text-foreground text-center">76%</div>
              </div>
            )}
        {!minimizedCards.has('cumprimentoPrazos') && <>
                <div className="space-y-2 mb-4">
                  <h3 className="font-medium text-sm text-muted-foreground">
                    Taxa de Cumprimento de Tarefas no Prazo
                  </h3>
                  <div className="text-3xl font-bold text-foreground">
                    76%
                  </div>
                  <p className="text-xs text-muted-foreground">
                    melhoria significativa
                  </p>
                </div>
                 <div className="flex flex-col gap-4">
                  <div className="h-40 sm:h-48 md:h-56 lg:h-60 xl:h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={data.cumprimentoPrazos} cx="50%" cy="50%" innerRadius="55%" outerRadius="80%" dataKey="value">
                          {data.cumprimentoPrazos.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                        </Pie>
                        <Tooltip content={<PieChartTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-3">
                    {data.cumprimentoPrazos.map((item, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: item.color }}
                        />
                        <span className="text-sm font-medium">{item.name}</span>
                        <span className="text-sm text-muted-foreground">{item.percentage}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>}
          </Card>}

        {config.tarefasAtrasadas && !hiddenCards.has('tarefasAtrasadas') && <Card className={`hover:shadow-lg transition-all duration-300 cursor-pointer bg-white border ${minimizedCards.has('tarefasAtrasadas') ? 'p-4' : 'p-6'}`}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-gray-50 shadow-sm">
                  <AlertTriangle className="h-6 w-6 text-primary" />
                </div>
                {minimizedCards.has('tarefasAtrasadas') && <h3 className="font-medium text-sm text-muted-foreground">
                    Tarefas em Atraso
                  </h3>}
              </div>
              <div className="flex items-center gap-2">
                {!minimizedCards.has('tarefasAtrasadas') && <Badge variant="default" className="gap-1">
                    <TrendingUp className="h-3 w-3" />
                    -7
                  </Badge>}
                <button onClick={() => toggleMinimize('tarefasAtrasadas')} className="inline-flex h-11 w-11 items-center justify-center rounded transition-colors hover:bg-muted sm:h-7 sm:w-7">
                  {minimizedCards.has('tarefasAtrasadas') ? <Maximize2 className="h-4 w-4 text-gray-500" /> : <Minimize2 className="h-4 w-4 text-gray-500" />}
                </button>
                <button onClick={() => hideCard('tarefasAtrasadas')} className="inline-flex h-11 w-11 items-center justify-center rounded transition-colors hover:bg-muted sm:h-7 sm:w-7">
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              </div>
            </div>
            {minimizedCards.has('tarefasAtrasadas') && (
              <div className="py-2">
                <div className="text-2xl font-bold text-foreground text-center">23</div>
              </div>
            )}
        {!minimizedCards.has('tarefasAtrasadas') && <>
                <div className="space-y-2 mb-4">
                  <h3 className="font-medium text-sm text-muted-foreground">
                    Tarefas em Atraso
                  </h3>
                  <div className="text-3xl font-bold text-foreground">
                    23
                  </div>
                  <p className="text-xs text-muted-foreground">
                    redução vs. último período
                  </p>
                </div>
                <div className="flex flex-col gap-4">
                  <div className="h-40 sm:h-48 md:h-56 lg:h-60 xl:h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={data.tarefasAtrasadas} cx="50%" cy="50%" innerRadius="55%" outerRadius="80%" dataKey="value">
                          {data.tarefasAtrasadas.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                        </Pie>
                        <Tooltip content={<PieChartTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-3">
                    {data.tarefasAtrasadas.map((item, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: item.color }}
                        />
                        <span className="text-sm font-medium">{item.name}</span>
                        <span className="text-sm text-muted-foreground">{item.percentage}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>}
          </Card>}

        {/* Tarefas por Status - Conforme Relatórios 2 */}
        {config.tarefasStatus && !hiddenCards.has('tarefasStatus') && <Card className={`hover:shadow-lg transition-all duration-300 cursor-pointer bg-white border ${minimizedCards.has('tarefasStatus') ? 'p-4 col-span-1' : 'p-6 col-span-1 md:col-span-2'}`}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-gray-50 shadow-sm">
                  <BarChart3 className="h-6 w-6 text-primary" />
                </div>
                {minimizedCards.has('tarefasStatus') && <h3 className="font-medium text-sm text-muted-foreground">
                    Tarefa por Status
                  </h3>}
              </div>
              <div className="flex items-center gap-2">
                {!minimizedCards.has('tarefasStatus') && <Badge variant="default" className="gap-1">
                    <TrendingUp className="h-3 w-3" />
                    +12
                  </Badge>}
                <button onClick={() => toggleMinimize('tarefasStatus')} className="inline-flex h-11 w-11 items-center justify-center rounded transition-colors hover:bg-muted sm:h-7 sm:w-7">
                  {minimizedCards.has('tarefasStatus') ? <Maximize2 className="h-4 w-4 text-gray-500" /> : <Minimize2 className="h-4 w-4 text-gray-500" />}
                </button>
                <button onClick={() => hideCard('tarefasStatus')} className="inline-flex h-11 w-11 items-center justify-center rounded transition-colors hover:bg-muted sm:h-7 sm:w-7">
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              </div>
            </div>
            {minimizedCards.has('tarefasStatus') && (
              <div className="py-2">
                <div className="text-2xl font-bold text-foreground text-center">156</div>
              </div>
            )}
        {!minimizedCards.has('tarefasStatus') && <>
                <div className="space-y-2 mb-4">
                  <h3 className="font-medium text-sm text-muted-foreground">
                    Tarefa por Status
                  </h3>
                  <div className="text-3xl font-bold text-foreground">
                    156
                  </div>
                  <p className="text-xs text-muted-foreground">
                    distribuição atual
                  </p>
                </div>
                <ResponsiveContainer width="100%" height={120}>
                  <BarChart data={data.tarefasPorStatus} margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5
            }}>
                    <XAxis dataKey="name" fontSize={12} />
                    <Tooltip content={<ChartTooltip showPercentage={true} />} />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      {data.tarefasPorStatus.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                      <LabelList dataKey="value" position="top" fontSize={12} fill="hsl(var(--foreground))" fontWeight="600" />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </>}
          </Card>}

        {/* Equipamentos - Novo bloco com gráfico de pizza */}
        {config.equipamentos && !hiddenCards.has('equipamentos') && <Card className={`hover:shadow-lg transition-all duration-300 cursor-pointer bg-white border ${minimizedCards.has('equipamentos') ? 'p-4' : 'p-6'}`}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-gray-50 shadow-sm">
                  <Wrench className="h-6 w-6 text-primary" />
                </div>
                {minimizedCards.has('equipamentos') && <h3 className="font-medium text-sm text-muted-foreground">
                    Equipamentos
                  </h3>}
              </div>
              <div className="flex items-center gap-2">
                {!minimizedCards.has('equipamentos') && <Badge variant="default" className="gap-1">
                    <TrendingUp className="h-3 w-3" />
                    +3
                  </Badge>}
                <button onClick={() => toggleMinimize('equipamentos')} className="inline-flex h-11 w-11 items-center justify-center rounded transition-colors hover:bg-muted sm:h-7 sm:w-7">
                  {minimizedCards.has('equipamentos') ? <Maximize2 className="h-4 w-4 text-gray-500" /> : <Minimize2 className="h-4 w-4 text-gray-500" />}
                </button>
                <button onClick={() => hideCard('equipamentos')} className="inline-flex h-11 w-11 items-center justify-center rounded transition-colors hover:bg-muted sm:h-7 sm:w-7">
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              </div>
            </div>
            {minimizedCards.has('equipamentos') && (
              <div className="py-2">
                <div className="text-2xl font-bold text-foreground text-center">45/52</div>
              </div>
            )}
        {!minimizedCards.has('equipamentos') && <>
                <div className="space-y-2 mb-4">
                  <h3 className="font-medium text-sm text-muted-foreground">
                    Equipamentos
                  </h3>
                  <div className="text-3xl font-bold text-foreground">
                    45/52
                  </div>
                  <p className="text-xs text-muted-foreground">
                    próprio / locado
                  </p>
                </div>
                <div className="flex flex-col gap-4">
                  <div className="h-40 sm:h-48 md:h-56 lg:h-60 xl:h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={data.equipamentos} cx="50%" cy="50%" innerRadius="55%" outerRadius="80%" dataKey="value">
                          {data.equipamentos.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                        </Pie>
                        <Tooltip content={<PieChartTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-3">
                    {data.equipamentos.map((item, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: item.color }}
                        />
                        <span className="text-sm font-medium">{item.name}</span>
                        <span className="text-sm text-muted-foreground">{item.percentage}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>}
          </Card>}
      </div>
    </div>;
};